import { useState, useEffect, useMemo } from "react";
import { debounce } from "throttle-debounce";

import variables from "../_variables.scss";

type WindowSizeReturn = {
  width: number;
  height: number;
  mobile: boolean;
  medium: boolean;
  smallTablet: boolean;
  tablet: boolean;
  smallDesktop: boolean;
  desktop: boolean;
};

/**
 * Creates a singleton window size hook that efficiently tracks viewport dimensions
 * and provides derived breakpoint values.
 *
 * Benefits:
 * - Only one resize event listener regardless of how many components use the hook
 * - Properly debounced updates to prevent excessive rerenders
 * - Shared calculations for all components
 * - Proper cleanup when components unmount
 */

const createWindowSizeHook = () => {
  const state = {
    dimensions: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    listeners: new Set<() => void>()
  };

  // Create a single debounced resize handler
  const debouncedHandleResize = debounce(300, () => {
    state.dimensions = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Notify all subscribed components
    state.listeners.forEach(listener => listener());
  });

  window.addEventListener("resize", debouncedHandleResize);

  // Return the hook implementation
  return function useWindowSize(): WindowSizeReturn {
    const [dimensions, setDimensions] = useState(state.dimensions);

    useEffect(() => {
      const updateDimensions = () => {
        setDimensions(state.dimensions);
      };

      updateDimensions();
      state.listeners.add(updateDimensions);

      return () => {
        state.listeners.delete(updateDimensions);
      };
    }, []);

    const { width, height } = dimensions;

    const mobile = useMemo(
      () => width <= parseInt(variables.breakpointMobile, 10),
      [width]
    );

    const medium = useMemo(
      () => width <= parseInt(variables.breakpointMedium, 10),
      [width]
    );

    const smallTablet = useMemo(
      () => width <= parseInt(variables.breakpointLaptop, 10),
      [width]
    );

    const tablet = useMemo(
      () => width <= parseInt(variables.breakpointLargeLaptop, 10),
      [width]
    );

    const smallDesktop = useMemo(
      () => width <= parseInt(variables.breakpointHD, 10),
      [width]
    );

    const desktop = useMemo(
      () => width >= parseInt(variables.breakpointHD, 10),
      [width]
    );

    return {
      width,
      height,
      mobile,
      medium,
      smallTablet,
      tablet,
      smallDesktop,
      desktop,
    };
  };
};

// Create a singleton instance of the window size hook
const useWindowSize = createWindowSizeHook();

export default useWindowSize;
